import { type ReactNode } from 'react'
import { Box, Group } from '@mantine/core'
import { AppBadge, AppSkeleton } from '../../app/common/ui'
import { IoEarthOutline, IoLinkOutline, IoLockClosedOutline } from 'react-icons/io5'
import type { IconType } from 'react-icons'
import type { CacheVisibility } from '../../app/models/cache'
import { AppText } from '../../app/common/ui'
import { uiTokens } from '../../app/theme/tokens'

/** Shared presentation helpers + primitives for the Caches feature. */

export interface CacheVisibilityMeta {
  value: CacheVisibility;
  label: string;
  /** One-line explanation shown in the settings picker and create form. */
  description: string;
  /** Mantine color name for badges/accents. */
  color: string;
  Icon: IconType;
}

/** Ordered most-open → most-closed for the visibility picker. */
export const CACHE_VISIBILITY_OPTIONS: CacheVisibilityMeta[] = [
  {
    value: 'Public',
    label: 'Public',
    description: 'Listed in browse and search. Anyone can view the board and join.',
    color: 'green',
    Icon: IoEarthOutline
  },
  {
    value: 'Unlisted',
    label: 'Unlisted',
    description: 'Hidden from browse and search. Anyone with the link can view and join.',
    color: 'blue',
    Icon: IoLinkOutline
  },
  {
    value: 'Private',
    label: 'Private',
    description: 'Hidden from browse. The board is members-only — but anyone with the link can still self-join.',
    color: 'orange',
    Icon: IoLockClosedOutline
  }
]

export function visibilityMeta (visibility: CacheVisibility | string | null | undefined): CacheVisibilityMeta {
  return CACHE_VISIBILITY_OPTIONS.find((o) => o.value === visibility) ?? CACHE_VISIBILITY_OPTIONS[0]
}

/** Small badge communicating who can see a cache. Public caches render nothing when hidePublic. */
export function CacheVisibilityBadge ({
  visibility,
  size = 'sm',
  hidePublic = false
}: {
  visibility: CacheVisibility | string | null | undefined;
  size?: string;
  hidePublic?: boolean;
}) {
  const meta = visibilityMeta(visibility)
  if (hidePublic && meta.value === 'Public') return null
  const Icon = meta.Icon
  return (
    <AppBadge
      variant="light"
      color={meta.color}
      size={size}
      radius="sm"
      leftSection={<Icon size={11} />}
      styles={{
        root: { textTransform: 'none', fontWeight: 600 },
        leftSection: { display: 'flex', alignItems: 'center' }
      }}
    >
      {meta.label}
    </AppBadge>
  )
}

/* ------------------------------------------------------------------ */
/* Cover / emblem                                                      */
/* ------------------------------------------------------------------ */

/**
 * Effective cover for a cache: uploaded cover wins, otherwise fall back to the
 * linked registry model's image. Returns null when neither exists (caller shows a branded emblem).
 */
export function resolveCacheCover (cache: {
  coverImageUrl?: string | null;
  firearmModelCoverImageUrl?: string | null;
}): string | null {
  return cache.coverImageUrl?.trim() || cache.firearmModelCoverImageUrl?.trim() || null
}

function hashString (input: string): number {
  let h = 0
  for (let i = 0; i < input.length; i++) h = (h * 31 + input.charCodeAt(i)) >>> 0
  return h
}

export function monogram (name: string | null | undefined): string {
  const cleaned = (name ?? '').trim()
  if (!cleaned) return '?'
  const words = cleaned.split(/\s+/).filter(Boolean)
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[words.length - 1][0]).toUpperCase()
}

/** Deterministic on-brand ember glow — same cache always renders identically, but caches differ subtly. */
function emblemBackground (seed: string): string {
  const anchors = ['18% 12%', '82% 16%', '30% 82%', '74% 78%', '50% 8%']
  const anchor = anchors[hashString(seed) % anchors.length]
  return [
    `radial-gradient(115% 115% at ${anchor}, rgba(var(--gs-accent-rgb), 0.22), transparent 55%)`,
    'linear-gradient(150deg, var(--gs-surface2) 0%, var(--gs-bg) 130%)'
  ].join(', ')
}

/**
 * Branded placeholder rendered when a cache has no cover image: a gunmetal panel with an
 * ember glow and a large faded monogram watermark. Looks intentional rather than "missing image".
 * Accepts either a fixed `height` or a responsive `aspectRatio` (e.g. "16 / 7").
 */
export function CacheEmblem ({
  name,
  seed,
  height,
  aspectRatio,
  radius = uiTokens.radius.md,
  fontSize
}: {
  name: string | null | undefined;
  seed?: string;
  height?: number | string;
  aspectRatio?: string;
  radius?: number;
  fontSize?: number;
}) {
  const sizing = aspectRatio ? { aspectRatio, width: '100%' } : { height: height ?? 160 }
  const computedFont = fontSize ?? (typeof height === 'number' ? Math.round(height * 0.42) : 72)
  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: radius,
        overflow: 'hidden',
        background: emblemBackground(seed ?? name ?? 'cache'),
        ...sizing
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: uiTokens.typography.display,
          fontWeight: 700,
          fontSize: computedFont,
          letterSpacing: 1,
          color: 'rgba(230,237,243,0.13)',
          userSelect: 'none'
        }}
      >
        {monogram(name)}
      </Box>
    </Box>
  )
}

/**
 * Cover banner that always shows the *whole* image (never a zoomed-in crop): the photo is rendered
 * `contain`, and the surrounding space is filled with a blurred, dimmed copy of the same image so
 * any aspect ratio looks intentional. Falls back to a branded emblem when there's no cover, or a
 * skeleton while uploading. Pass `aspectRatio` (e.g. "16 / 7") for a responsive banner, or `height`.
 */
export function CacheCover ({
  url,
  name,
  seed,
  height = 160,
  aspectRatio,
  radius = uiTokens.radius.md,
  overlay,
  loading = false,
  onImageClick
}: {
  url: string | null;
  name?: string | null;
  seed?: string;
  height?: number;
  aspectRatio?: string;
  radius?: number;
  overlay?: ReactNode;
  loading?: boolean;
  /** When provided and a cover image exists, the cover becomes a button that opens the image (e.g. a lightbox). */
  onImageClick?: (url: string) => void;
}) {
  const sizing = aspectRatio ? { aspectRatio, width: '100%' } : { height }

  if (loading) {
    return <AppSkeleton radius={radius} sx={{ ...sizing }} />
  }

  if (!url) {
    return (
      <Box sx={{ position: 'relative' }}>
        <CacheEmblem name={name} seed={seed} height={aspectRatio ? undefined : height} aspectRatio={aspectRatio} radius={radius} />
        {overlay && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: uiTokens.spacing.md }}>
            {overlay}
          </Box>
        )}
      </Box>
    )
  }

  const clickable = !!onImageClick
  const clickProps = clickable
    ? {
        component: 'button' as const,
        type: 'button' as const,
        'aria-label': 'View cover image',
        onClick: () => onImageClick?.(url)
      }
    : {}

  return (
    <Box
      {...clickProps}
      sx={{
        position: 'relative',
        borderRadius: radius,
        overflow: 'hidden',
        background: 'var(--gs-surface2)',
        display: 'block',
        width: '100%',
        padding: 0,
        border: 'none',
        textAlign: 'inherit',
        font: 'inherit',
        color: 'inherit',
        cursor: clickable ? 'zoom-in' : 'default',
        ...sizing
      }}
    >
      {/* Blurred, dimmed copy fills the frame so there are no empty bars around the contained image. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url("${url}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(32px) saturate(1.15) brightness(0.5)',
          transform: 'scale(1.25)'
        }}
      />
      {/* The full image, never cropped. */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url("${url}")`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      />
      {/* Bottom scrim for overlay legibility. */}
      {overlay && (
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(0,0,0,0.72) 100%)' }} />
      )}
      {overlay && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: uiTokens.spacing.md }}>
          {overlay}
        </Box>
      )}
    </Box>
  )
}

/* ------------------------------------------------------------------ */
/* Shared style helpers (token-driven)                                 */
/* ------------------------------------------------------------------ */

/** Consistent interactive card: subtle lift + accent border + shadow on hover. */
export const cacheCardHoverSx = {
  transition: `transform ${uiTokens.motion.normal}, box-shadow ${uiTokens.motion.normal}, border-color ${uiTokens.motion.normal}`,
  '&:hover': {
    transform: 'translateY(-3px)',
    borderColor: 'var(--gs-border-bright)',
    boxShadow: uiTokens.elevation.md
  }
}

/** Uppercase section label used across the feature. */
export function SectionLabel ({ children }: { children: ReactNode }) {
  return (
    <AppText
      size="xs"
      weight={700}
      color="dimmed"
      sx={{ textTransform: 'uppercase', letterSpacing: 1.2 }}
    >
      {children}
    </AppText>
  )
}

/** Icon + value stat pill for headers. */
export function StatChip ({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <Group spacing={6} sx={{ color: 'var(--gs-muted)' }}>
      {icon}
      <AppText size="sm" color="dimmed">{children}</AppText>
    </Group>
  )
}

export function timeAgo (iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const m = Math.floor(ms / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 30) return `${d}d ago`
  return new Date(iso).toLocaleDateString()
}
